import { ChangeEvent, FocusEventHandler, useEffect, useState } from 'react'
import { useUI } from '@components/ui/context'
import usePrice from '@framework/product/use-price'
import useUpdateItem from '@framework/cart/use-update-item'
import useRemoveItem from '@framework/cart/use-remove-item'
import Quantity from '@components/ui/Quantity'
import cn from 'clsx'
import { Minus, Plus, Trash } from '@components/icons'
import dataLayer from '@utils/dataLayer'
import type { LineItem } from '@framework/types/cart'

const ns = 'cart-item'

type ItemOption = {
  name: string
  nameId: number
  value: string
  valueId: number
}

const placeholderImg = '/product-img-placeholder.svg'

const CartItem = ({
  item,
  variant = 'default',
  currencyCode,
  ...rest
}: {
  variant?: 'default' | 'display'
  item: LineItem
  currencyCode: string
}) => {
  const rootClassNames = cn({
    [`${ns}`]: true,
    [`${ns}--remove`]: item.quantity <= 0,
  })

  const getDiscount = (price: number | null, quantity: number | null) => {
    if (!price || !quantity) return null

    return Number(price * quantity)
  }

  // const { closeSidebarIfPresent } = useUI()
  const [_, setRemoving] = useState(false)
  const [quantity, setQuantity] = useState<number>(item.quantity)
  const [initialQuantity, setInitialQuantity] = useState<number>(item.quantity)
  const removeItem = useRemoveItem()
  const updateItem = useUpdateItem({ item })

  const image = item?.variant?.image?.url || placeholderImg
  const name = item?.name || ''
  const discount = getDiscount(
    item?.variant?.price || item?.variant?.listPrice,
    item?.quantity
  )

  const price = item?.totalAmount
  const description =
    item?.variant?.name === 'Default Title' ? '' : item?.variant?.name
  const shipment = item?.attributes?.[0]?.value

  useEffect(() => {
    // Reset the quantity state if the item quantity changes
    if (initialQuantity !== item.quantity) {
      setInitialQuantity(item.quantity)

      if (item.quantity !== quantity) {
        setQuantity(item.quantity)
      }
    }
  }, [initialQuantity, quantity, item.quantity])

  /*
  const handleChange = async ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    setQuantity(Number(value))
    await updateItem({ quantity: Number(value) })
  }
  */

  const updateQuantity = async (n = 1) => {
    const val = Number(quantity) + n

    if (n === -1 && 1 <= quantity) {
      await updateItem({ quantity: val })

      setQuantity(val)
    } else if (n === 1) {
      await updateItem({ quantity: val })

      setQuantity(val)
    }

    if (val === 0) {
      // Add event to data layer
      dataLayer().push(`remove_from_cart`, [
        {
          product: item,
          variant: item?.variant,
        },
      ])
    } else {
      // Add event to data layer
      dataLayer().push(`update_quantity`, [
        {
          product: item,
          variant: item?.variant,
          quantity: val,
        },
      ])
    }
  }

  const handleRemove = async () => {
    setRemoving(true)
    try {
      await removeItem(item)

      // Add event to data layer
      dataLayer().push(`remove_from_cart`, [
        {
          product: item,
          variant: item?.variant,
          quantity,
        },
      ])
    } catch (error) {
      setRemoving(false)
    }
  }

  const Counter = ({ quantity }: { quantity: number }) => {
    return (
      <div className={`${ns}__container-quantity-trash`}>
        <div className={`${ns}__quantity-btn`} onClick={() => handleRemove()}>
          <Trash />
        </div>
        <div className={`${ns}__container-quantity`}>
          <div
            className={`${ns}__quantity-btn`}
            onClick={() => updateQuantity(-1)}
          >
            <Minus width="16" height="16" className={`${ns}__quantity-btn`} />
          </div>
          {0 <= quantity && (
            <span className={`${ns}__quantity`}>{quantity}</span>
          )}
          <div
            className={`${ns}__quantity-btn`}
            onClick={() => updateQuantity(1)}
          >
            <Plus width="16" height="16" className={`${ns}__quantity-btn`} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <li className={rootClassNames}>
      <div className={`${ns}__container`}>
        <div className={`${ns}__subcontainer`}>
          <img
            className={`${ns}__image`}
            src={image}
            alt={name}
            width={110}
            height={110}
          />
          {/* TEXT  */}
          <div className={`${ns}__container-text`}>
            {/* NAME | PRICE */}
            <div className={`${ns}__subcontainer-text`}>
              <span className={`${ns}__name`}> {name}</span>
              <div className={`${ns}__subcontainer-price`}>
                {discount && price && discount > 0 && discount !== price && (
                  <span className={`${ns}__price-discount`}>
                    ${discount.toFixed(2)}
                  </span>
                )}
                {price && (
                  <span className={`${ns}__name`}>${price.toFixed(2)}</span>
                )}
              </div>
            </div>
            {/* DESCRIPTION | COUNTER */}
            <div
              className={`${ns}__subcontainer-text ${ns}__subcontainer-text-desktop`}
            >
              <div className={`${ns}__subcontainer-description`}>
                <span className={`${ns}__shipment`}>{shipment}</span>
                <span className={`${ns}__description`}>{description}</span>
              </div>
              <Counter quantity={quantity} />
            </div>
          </div>
        </div>
        {/* DESCRIPTION | COUNTER */}
        <div
          className={`${ns}__subcontainer-text ${ns}__subcontainer-text-mobile`}
        >
          <div className={`${ns}__subcontainer-description`}>
            <span className={`${ns}__shipment`}>{shipment}</span>
            <span className={`${ns}__description`}>{description}</span>
          </div>
          <Counter quantity={quantity} />
        </div>
      </div>
    </li>
  )
}

export default CartItem
